import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL } from '../lib/site-config.js';
import { track } from '../lib/analytics.js';

// Subscribe posts to our own /api/subscribe (Netlify Blobs store that the
// daily send reads) instead of Netlify Forms, and stays on-page with an
// inline confirmation instead of bouncing to Netlify's generic thank-you.
function SubscribeForm() {
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, 'bot-field': hp })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      // A signup is a request until the address confirms it, so say which
      // happened rather than promising mail that is waiting on a click.
      setConfirming(body.confirmation === 'sent');
      setStatus('done');
      track('subscribe');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  }

  if (status === 'done') {
    return (
      <p className="max-w-sm rounded-lg border border-marsh-200 bg-marsh-50 px-4 py-3 text-sm text-marsh-700">
        {confirming
          ? "Check your inbox. One click on the confirmation email and the brief starts arriving in the morning."
          : "You're in. The next brief lands in your inbox around 6:45 AM ET."}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="max-w-sm">
      <div className="flex gap-2">
        <input
          type="text"
          name="bot-field"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        {/* text-base below sm: 16px stops iOS Safari zooming the page on focus. */}
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 rounded-lg border border-navy-200 bg-white px-3 py-2 text-base text-navy-900 placeholder:text-navy-500 focus:border-navy-600 sm:text-sm"
        />
        <button
          type="submit"
          disabled={status === 'sending'}
          // Same disabled treatment as the payment button: navy-600 on navy-100
          // is 6.49:1. Cream on navy-300 (2.3:1) washed the label out.
          className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-cream-50 hover:bg-navy-800 disabled:bg-navy-100 disabled:text-navy-600"
        >
          {status === 'sending' ? 'Adding…' : 'Subscribe'}
        </button>
      </div>
      {status === 'error' && (
        <p className="mt-2 text-xs text-charlestonpink-600">
          {error || "That didn't go through."} Try again, or email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="-my-1.5 inline-block py-1.5 underline">{CONTACT_EMAIL}</a>.
        </p>
      )}
      {/* navy-500, not navy-400: #5d83ad on the cream page is 3.8:1, under the
          4.5:1 floor for 12px text. navy-500 is 5.7:1 and reads the same. */}
      <p className="mt-2 text-xs text-navy-500">
        One email a morning, unsubscribe anytime.{' '}
        <Link to="/privacy" className="-my-1.5 inline-block py-1.5 underline hover:text-navy-700">Privacy</Link>
      </p>
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="mt-12 pb-12 text-sm text-navy-500">
      {/* Thin-thick closing rule mirroring the masthead. */}
      <div className="border-t border-navy-900" />
      <div className="mb-8 mt-[3px] border-t-2 border-navy-900" />
      <div className="mb-6">
        <p className="mb-3 font-display text-base font-semibold text-navy-900">
          One brief, every morning by 6:30 ET.
        </p>
        <SubscribeForm />
      </div>
      {/* -my-1.5 py-1.5 on every footer link (the subscribe form's too): the
          16px links become 28px tap targets and the negative margins hand the
          height back, so the rows and their wrapping do not move. */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
        <p>
          © 2026 Charleston.Vacations · Mount Pleasant, SC ·{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="-my-1.5 inline-block py-1.5 hover:text-navy-900">
            {CONTACT_EMAIL}
          </a>{' '}
          · Conditions are estimates, not navigation guidance.
        </p>
        <nav className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/" className="-my-1.5 py-1.5 hover:text-navy-900">Today</Link>
          <Link to="/about" className="-my-1.5 py-1.5 hover:text-navy-900">About</Link>
          {/* Yesterday lives here rather than the masthead (2026-07-25). */}
          <Link to="/yesterday" className="-my-1.5 py-1.5 hover:text-navy-900">Yesterday</Link>
          <Link to="/guides" className="-my-1.5 py-1.5 hover:text-navy-900">Guides</Link>
          <Link to="/stay" className="-my-1.5 py-1.5 hover:text-navy-900">Stay</Link>
          <Link to="/ai" className="-my-1.5 py-1.5 hover:text-navy-900">For your AI</Link>
          <Link to="/privacy" className="-my-1.5 py-1.5 hover:text-navy-900">Privacy</Link>
          <Link to="/terms" className="-my-1.5 py-1.5 hover:text-navy-900">Terms</Link>
        </nav>
      </div>
    </footer>
  );
}
